import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiX, FiCopy, FiEdit, FiTrash2, FiShare2, FiMail, FiPlus, FiCheck, FiMinus } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import './ShareModal.css';

/** Разбор поля адресов: запятая, точка с запятой, пробел, перевод строки */
function parseRecipientEmailsInput(input) {
  const raw = String(input ?? '').trim();
  if (!raw) return [];
  const normalized = raw.replace(/[,;\n\r]+/g, ' ');
  const tokens = normalized.split(/\s+/).map((t) => t.trim()).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const t of tokens) {
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

/** URL публичной ссылки на домене фирмы вебинара (full_url с API). */
function getShareLinkFullUrl(link) {
  if (link?.full_url) {
    return link.full_url;
  }
  return `${window.location.origin}/webinar/share/${link.slug}/`;
}

function emptyQuizQuestion(order = 0) {
  return {
    text: '',
    question_type: 'single',
    is_required: true,
    order,
    options: [{ text: 'Вариант 1' }, { text: 'Вариант 2' }],
  };
}

function mapQuizQuestionsFromApi(questions) {
  return (questions || []).map((question, index) => ({
    text: question.text || '',
    question_type: question.question_type || 'single',
    is_required: question.is_required !== false,
    order: question.order ?? index,
    options: (question.options || []).map((option) => ({ text: option.text || '' })),
  }));
}

const ShareModal = ({ open, onClose, webinar }) => {
  const { isModerator } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    has_password: false,
    requires_registration: false,
    access_days: '',
    quiz_enabled: false,
    expires_at: '',
    expires_at_time: '',
  });
  const [emailList, setEmailList] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [showRegistrationFields, setShowRegistrationFields] = useState(false);
  const [editingLinkForFields, setEditingLinkForFields] = useState(null);
  const [registrationFields, setRegistrationFields] = useState([]);
  const [newRegistrationFields, setNewRegistrationFields] = useState([]);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const distributionLockedForCurrentUser =
    !!webinar?.employee_only_distribution && !isModerator;

  const buildSpeakersText = () => {
    const speakersList = [];
    if (webinar?.speaker_data) {
      speakersList.push(webinar.speaker_data);
    }
    if (Array.isArray(webinar?.additional_speakers_data)) {
      webinar.additional_speakers_data.forEach((speaker) => {
        if (!speaker?.id) return;
        if (!speakersList.some((existing) => existing.id === speaker.id)) {
          speakersList.push(speaker);
        }
      });
    }
    if (speakersList.length === 0) {
      return { names: 'Спикер', credentials: '' };
    }
    const names = speakersList.map((speaker) => speaker.full_name).filter(Boolean).join(', ');
    const credentials = speakersList
      .map((speaker) => speaker.credentials)
      .filter(Boolean)
      .join('\n');
    return { names, credentials };
  };

  useEffect(() => {
    if (open && webinar) {
      fetchLinks();
      setSelectedLinkId(null); // Сбрасываем выбор при открытии модального окна
    }
  }, [open, webinar]);

  useEffect(() => {
    // Автоматически выбираем первую активную ссылку при загрузке списка
    if (links.length > 0 && selectedLinkId === null) {
      const activeLink = links.find(l => l.is_active);
      if (activeLink) {
        setSelectedLinkId(activeLink.id);
      } else if (links[0]) {
        setSelectedLinkId(links[0].id);
      }
    }
  }, [links, selectedLinkId]);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`/api/webinars/${webinar.id}/share-links/`);
      setLinks(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки ссылок');
    }
  };

  const validateQuizForm = () => {
    if (!formData.requires_registration || !formData.quiz_enabled) return true;
    const validQuestions = quizQuestions.filter((question) => String(question.text || '').trim());
    if (!validQuestions.length) {
      toast.error('Добавьте хотя бы один вопрос для теста');
      return false;
    }
    for (const question of validQuestions) {
      if (question.question_type !== 'text') {
        const options = (question.options || []).filter((option) => String(option.text || '').trim());
        if (options.length < 1) {
          toast.error(`У вопроса «${question.text}» должен быть хотя бы один вариант ответа`);
          return false;
        }
      }
    }
    return true;
  };

  const handleCreateLink = async () => {
    if (!formData.name.trim()) {
      toast.error('Введите название ссылки');
      return;
    }
    if (!validateQuizForm()) {
      return;
    }

    setLoading(true);
    try {
      const data = {
        name: formData.name,
        requires_registration: formData.requires_registration || false,
        access_days: formData.requires_registration && formData.access_days
          ? Number(formData.access_days)
          : null,
        quiz_enabled: Boolean(formData.requires_registration && formData.quiz_enabled),
        expires_at: formData.expires_at && formData.expires_at_time
          ? `${formData.expires_at}T${formData.expires_at_time}:00`
          : null,
      };

      if (formData.has_password && formData.password) {
        data.password = formData.password;
      }

      const response = await axios.post(`/api/webinars/${webinar.id}/share-links/`, data);
      const newLink = response.data;
      
      // Если требуется регистрация и есть поля, создаем их
      if (formData.requires_registration && newRegistrationFields.length > 0) {
        for (const field of newRegistrationFields) {
          await axios.post(`/api/webinars/${webinar.id}/share-links/${newLink.id}/registration-fields/`, {
            field_name: field.field_name,
            field_type: field.field_type,
            is_required: field.is_required,
            order: field.order,
          });
        }
      }

      if (formData.requires_registration && formData.quiz_enabled) {
        await axios.put(`/api/webinars/${webinar.id}/share-links/${newLink.id}/quiz/`, {
          questions: quizQuestions,
        });
      }
      
      toast.success('Ссылка создана');
      setShowCreateForm(false);
      resetForm();
      fetchLinks();
    } catch (error) {
      console.error('Ошибка создания ссылки:', error.response?.data || error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || 
                          (typeof error.response?.data === 'object' ? JSON.stringify(error.response.data) : 'Ошибка создания ссылки');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLink = async () => {
    if (!formData.name.trim()) {
      toast.error('Введите название ссылки');
      return;
    }
    if (!validateQuizForm()) {
      return;
    }

    setLoading(true);
    try {
      const data = {
        name: formData.name,
        requires_registration: formData.requires_registration || false,
        access_days: formData.requires_registration && formData.access_days
          ? Number(formData.access_days)
          : null,
        quiz_enabled: Boolean(formData.requires_registration && formData.quiz_enabled),
        expires_at: formData.expires_at && formData.expires_at_time
          ? `${formData.expires_at}T${formData.expires_at_time}:00`
          : null,
      };

      if (formData.has_password && formData.password) {
        data.password = formData.password;
      } else if (!formData.has_password) {
        data.password = ''; // Удалить пароль
      }

      await axios.put(`/api/webinars/${webinar.id}/share-links/${editingLink.id}/`, data);
      
      // Обновляем поля регистрации если требуется
      if (formData.requires_registration) {
        // Получаем текущие поля
        const currentFieldsResponse = await axios.get(`/api/webinars/${webinar.id}/share-links/${editingLink.id}/registration-fields/`);
        const currentFields = currentFieldsResponse.data;
        
        // Удаляем старые поля
        for (const field of currentFields) {
          await axios.delete(`/api/webinars/${webinar.id}/share-links/${editingLink.id}/registration-fields/${field.id}/`);
        }
        
        // Создаем новые поля
        if (newRegistrationFields.length > 0) {
          for (const field of newRegistrationFields) {
            await axios.post(`/api/webinars/${webinar.id}/share-links/${editingLink.id}/registration-fields/`, {
              field_name: field.field_name,
              field_type: field.field_type,
              is_required: field.is_required,
              order: field.order,
            });
          }
        }
      } else {
        // Если регистрация отключена, удаляем все поля
        const currentFieldsResponse = await axios.get(`/api/webinars/${webinar.id}/share-links/${editingLink.id}/registration-fields/`);
        const currentFields = currentFieldsResponse.data;
        for (const field of currentFields) {
          await axios.delete(`/api/webinars/${webinar.id}/share-links/${editingLink.id}/registration-fields/${field.id}/`);
        }
      }

      await axios.put(`/api/webinars/${webinar.id}/share-links/${editingLink.id}/quiz/`, {
        questions: formData.requires_registration && formData.quiz_enabled ? quizQuestions : [],
      });
      
      toast.success('Ссылка обновлена');
      setEditingLink(null);
      resetForm();
      fetchLinks();
    } catch (error) {
      toast.error('Ошибка обновления ссылки');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту ссылку?')) {
      return;
    }

    try {
      await axios.delete(`/api/webinars/${webinar.id}/share-links/${linkId}/`);
      toast.success('Ссылка удалена');
      fetchLinks();
    } catch (error) {
      toast.error('Ошибка удаления ссылки');
    }
  };

  const handleCopyLink = async (link) => {
    const fullUrl = getShareLinkFullUrl(link);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = fullUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Ссылка скопирована');
    } catch (error) {
      toast.error('Ошибка копирования');
    }
  };

  const handleCopyToMessenger = async (linkId = null) => {
    if (distributionLockedForCurrentUser) {
      toast.error('Этот вебинар можно отправлять только администратору');
      return;
    }
    const linkToUse = linkId 
      ? links.find(l => l.id === linkId)
      : links.find(l => l.id === selectedLinkId) || links.find(l => l.is_active) || links[0];
    
    if (!linkToUse) {
      toast.error('Выберите ссылку для отправки');
      return;
    }
    
    const link = linkToUse;
    const fullUrl = getShareLinkFullUrl(link);
    const speakersText = buildSpeakersText();
    
    let text = `Запись доступна.\n`;
    text += `Дата: ${new Date(webinar.recording_date).toLocaleDateString('ru-RU')}\n`;
    text += `Тема: ${webinar.title}\n`;
    text += `Спикеры: ${speakersText.names}`;
    if (speakersText.credentials) {
      text += `\nРегалии:\n${speakersText.credentials}`;
    }
    text += `\nСсылка: ${fullUrl}`;
    
    if (link.expires_at) {
      const expiresDate = new Date(link.expires_at);
      text += `\nДоступна до: ${expiresDate.toLocaleString('ru-RU')}`;
    }
    
    if (link.has_password) {
      text += `\nУточняйте пароль у Вашего менеджера`;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Текст скопирован в буфер обмена');
    } catch (error) {
      toast.error('Ошибка копирования');
    }
  };

  const handleSendEmail = async (linkId = null) => {
    if (distributionLockedForCurrentUser) {
      toast.error('Этот вебинар можно отправлять только администратору');
      return;
    }
    const emails = parseRecipientEmailsInput(emailList);
    if (emails.length === 0) {
      toast.error('Введите хотя бы один email');
      return;
    }

    if (!links.length) {
      toast.error('Сначала создайте ссылку');
      return;
    }

    // Используем выбранную ссылку или первую активную/первую в списке
    const link = linkId 
      ? links.find(l => l.id === linkId)
      : links.find(l => l.id === selectedLinkId) || links.find(l => l.is_active) || links[0];
    
    if (!link) {
      toast.error('Выберите ссылку для отправки');
      return;
    }
    const fullUrl = getShareLinkFullUrl(link);
    setLoading(true);
    try {
      const response = await axios.post(`/api/webinars/${webinar.id}/send-share-email/`, {
        emails,
        share_link_id: link.id,
        share_url: fullUrl,
      });
      toast.success(
        response.data?.message || `Письмо отправлено (${emails.length} адресов в скрытой копии)`
      );
      setShowEmailForm(false);
      setEmailList('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка отправки email');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      password: '',
      has_password: false,
      requires_registration: false,
      access_days: '',
      quiz_enabled: false,
      expires_at: '',
      expires_at_time: '',
    });
    setNewRegistrationFields([]);
    setQuizQuestions([]);
  };

  const fetchRegistrationFields = async (linkId) => {
    try {
      const response = await axios.get(`/api/webinars/${webinar.id}/share-links/${linkId}/registration-fields/`);
      setRegistrationFields(response.data);
    } catch (error) {
      console.error('Ошибка загрузки полей регистрации:', error);
      setRegistrationFields([]);
    }
  };

  const handleSaveRegistrationField = async (field) => {
    try {
      if (field.id) {
        // Обновление
        await axios.put(`/api/webinars/${webinar.id}/share-links/${editingLinkForFields.id}/registration-fields/${field.id}/`, field);
      } else {
        // Создание
        await axios.post(`/api/webinars/${webinar.id}/share-links/${editingLinkForFields.id}/registration-fields/`, field);
      }
      fetchRegistrationFields(editingLinkForFields.id);
      toast.success('Поле сохранено');
    } catch (error) {
      toast.error('Ошибка сохранения поля');
    }
  };

  const handleDeleteRegistrationField = async (fieldId) => {
    try {
      await axios.delete(`/api/webinars/${webinar.id}/share-links/${editingLinkForFields.id}/registration-fields/${fieldId}/`);
      fetchRegistrationFields(editingLinkForFields.id);
      toast.success('Поле удалено');
    } catch (error) {
      toast.error('Ошибка удаления поля');
    }
  };

  const addDefaultFields = async () => {
    if (!editingLinkForFields) return;
    
    const defaultFields = [
      { field_name: 'Фамилия', field_type: 'text', is_required: true, order: 1 },
      { field_name: 'Имя', field_type: 'text', is_required: true, order: 2 },
      { field_name: 'Email', field_type: 'text', is_required: true, order: 3 },
      { field_name: 'Отчество', field_type: 'text', is_required: false, order: 4 },
      { field_name: 'Компания', field_type: 'text', is_required: false, order: 5 },
      { field_name: 'Должность', field_type: 'text', is_required: false, order: 6 },
      { field_name: 'Город', field_type: 'text', is_required: false, order: 7 },
      { field_name: 'Голограмма препарата', field_type: 'hologram', is_required: false, order: 8 },
    ];

    try {
      for (const field of defaultFields) {
        await axios.post(`/api/webinars/${webinar.id}/share-links/${editingLinkForFields.id}/registration-fields/`, field);
      }
      fetchRegistrationFields(editingLinkForFields.id);
      toast.success('Стандартные поля добавлены');
    } catch (error) {
      toast.error('Ошибка добавления полей');
    }
  };

  const addDefaultFieldsToForm = () => {
    const defaultFields = [
      { field_name: 'Фамилия', field_type: 'text', is_required: true, order: 1 },
      { field_name: 'Имя', field_type: 'text', is_required: true, order: 2 },
      { field_name: 'Email', field_type: 'text', is_required: true, order: 3 },
      { field_name: 'Отчество', field_type: 'text', is_required: false, order: 4 },
      { field_name: 'Компания', field_type: 'text', is_required: false, order: 5 },
      { field_name: 'Должность', field_type: 'text', is_required: false, order: 6 },
      { field_name: 'Город', field_type: 'text', is_required: false, order: 7 },
      { field_name: 'Голограмма препарата', field_type: 'hologram', is_required: false, order: 8 },
    ];
    setNewRegistrationFields(defaultFields);
  };

  const addHologramField = () => {
    const hologramFields = newRegistrationFields.filter(f => f.field_type === 'hologram');
    const maxOrder = Math.max(...newRegistrationFields.map(f => f.order), 0);
    const newField = {
      field_name: `Голограмма препарата ${hologramFields.length + 1}`,
      field_type: 'hologram',
      is_required: false,
      order: maxOrder + 1,
    };
    setNewRegistrationFields([...newRegistrationFields, newField]);
  };

  const removeRegistrationField = (index) => {
    setNewRegistrationFields(newRegistrationFields.filter((_, i) => i !== index));
  };

  const updateRegistrationField = (index, updates) => {
    const newFields = [...newRegistrationFields];
    newFields[index] = { ...newFields[index], ...updates };
    setNewRegistrationFields(newFields);
  };

  const updateQuizQuestion = (index, updates) => {
    setQuizQuestions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const updateQuizOption = (questionIndex, optionIndex, text) => {
    setQuizQuestions((prev) => {
      const next = [...prev];
      const options = [...(next[questionIndex].options || [])];
      options[optionIndex] = { ...options[optionIndex], text };
      next[questionIndex] = { ...next[questionIndex], options };
      return next;
    });
  };

  const addQuizQuestion = () => {
    setQuizQuestions((prev) => [...prev, emptyQuizQuestion(prev.length)]);
  };

  const removeQuizQuestion = (index) => {
    setQuizQuestions((prev) => prev.filter((_, i) => i !== index).map((question, order) => ({ ...question, order })));
  };

  const addQuizOption = (questionIndex) => {
    setQuizQuestions((prev) => {
      const next = [...prev];
      const options = [...(next[questionIndex].options || [])];
      options.push({ text: `Вариант ${options.length + 1}` });
      next[questionIndex] = { ...next[questionIndex], options };
      return next;
    });
  };

  const removeQuizOption = (questionIndex, optionIndex) => {
    setQuizQuestions((prev) => {
      const next = [...prev];
      const options = (next[questionIndex].options || []).filter((_, i) => i !== optionIndex);
      next[questionIndex] = { ...next[questionIndex], options };
      return next;
    });
  };

  const startEdit = async (link) => {
    setEditingLink(link);
    const expiresDate = link.expires_at ? new Date(link.expires_at) : null;
    setFormData({
      name: link.name,
      password: '',
      has_password: link.has_password,
      requires_registration: link.requires_registration || false,
      access_days: link.access_days || '',
      quiz_enabled: Boolean(link.quiz_enabled),
      expires_at: expiresDate ? expiresDate.toISOString().split('T')[0] : '',
      expires_at_time: expiresDate ? expiresDate.toTimeString().slice(0, 5) : '',
    });
    // Загружаем существующие поля регистрации при редактировании
    if (link.requires_registration && link.id) {
      try {
        const response = await axios.get(`/api/webinars/${webinar.id}/share-links/${link.id}/registration-fields/`);
        setNewRegistrationFields(response.data.map(f => ({ 
          field_name: f.field_name,
          field_type: f.field_type,
          is_required: f.is_required,
          order: f.order
        })));
      } catch (error) {
        console.error('Ошибка загрузки полей:', error);
        setNewRegistrationFields([]);
      }
      try {
        const quizResponse = await axios.get(`/api/webinars/${webinar.id}/share-links/${link.id}/quiz/`);
        const mapped = mapQuizQuestionsFromApi(quizResponse.data);
        setQuizQuestions(mapped);
      } catch (error) {
        console.error('Ошибка загрузки вопросов теста:', error);
        setQuizQuestions(link.quiz_enabled ? [emptyQuizQuestion(0)] : []);
      }
    } else {
      setNewRegistrationFields([]);
      setQuizQuestions([]);
    }
    setShowCreateForm(true);
  };

  const cancelEdit = () => {
    setEditingLink(null);
    resetForm();
    setShowCreateForm(false);
  };

  if (!open) return null;

  return (
    <div className="share-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="share-modal-content">
        <div className="share-modal-header">
          <h2>Поделиться ссылкой на видео</h2>
          <button className="share-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="share-modal-body">
          {/* Список существующих ссылок */}
          <div className="share-links-section">
            <h3>Существующие ссылки</h3>
            {links.length === 0 ? (
              <p className="share-empty-state">Нет созданных ссылок</p>
            ) : (
              <div className="share-links-list">
                {links.map((link) => (
                  <div key={link.id} className="share-link-item">
                    <div className="share-link-info">
                      <div className="share-link-name">{link.name}</div>
                      <div className="share-link-url">
                        {getShareLinkFullUrl(link)}
                      </div>
                      {link.expires_at && (
                        <div className="share-link-expires">
                          Ограничение по времени: {new Date(link.expires_at).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                      <div className="share-link-meta">
                        {link.has_password && <span className="share-link-badge share-link-badge-password">🔒 Пароль</span>}
                        {link.requires_registration && <span className="share-link-badge share-link-badge-registration">📝 С регистрацией</span>}
                        {link.requires_registration && link.access_days ? (
                          <span className="share-link-badge">Доступ {link.access_days} дн. после регистрации</span>
                        ) : null}
                        {link.quiz_enabled && (link.quiz_questions_count || 0) > 0 ? (
                          <span className="share-link-badge">Тест: {link.quiz_questions_count} вопр.</span>
                        ) : null}
                        <span className="share-link-badge">Использований: {link.access_count}</span>
                        {link.created_by_email && (
                          <span className="share-link-badge share-link-badge-creator">
                            Создал: {link.created_by_email}
                          </span>
                        )}
                        {link.created_at && (
                          <span className="share-link-badge share-link-badge-date">
                            {new Date(link.created_at).toLocaleDateString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="share-link-actions">
                      <button
                        className="share-link-btn"
                        onClick={() => handleCopyLink(link)}
                        title="Копировать ссылку"
                      >
                        <FiCopy />
                      </button>
                      <button
                        className="share-link-btn"
                        onClick={() => startEdit(link)}
                        title="Редактировать"
                      >
                        <FiEdit />
                      </button>
                      <button
                        className="share-link-btn"
                        onClick={() => {
                          setEditingLinkForFields(link);
                          fetchRegistrationFields(link.id);
                          setShowRegistrationFields(true);
                        }}
                        title="Настройки регистрации"
                      >
                        📝
                      </button>
                      <button
                        className="share-link-btn share-link-btn-danger"
                        onClick={() => handleDeleteLink(link.id)}
                        title="Удалить"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Выбор ссылки для отправки */}
          {links.length > 1 && (
            <div className="share-link-selector-section">
              <h3>Выберите ссылку для отправки:</h3>
              <div className="share-link-radio-group">
                {links.map((link) => (
                  <label key={link.id} className="share-link-radio-label">
                    <input
                      type="radio"
                      name="selectedLink"
                      value={link.id}
                      checked={selectedLinkId === link.id}
                      onChange={(e) => setSelectedLinkId(parseInt(e.target.value))}
                    />
                    <div className="share-link-radio-content">
                      <span className="share-link-radio-name">{link.name}</span>
                      {link.created_by_email && (
                        <span className="share-link-radio-creator">Создал: {link.created_by_email}</span>
                      )}
                      {link.expires_at && (
                        <span className="share-link-radio-expires">
                          До {new Date(link.expires_at).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                      {link.has_password && (
                        <span className="share-link-radio-password">🔒 С паролем</span>
                      )}
                      {link.requires_registration && (
                        <span className="share-link-radio-registration">
                          📝 С регистрацией{link.access_days ? ` · ${link.access_days} дн.` : ''}
                          {link.quiz_enabled && (link.quiz_questions_count || 0) > 0 ? ' · тест' : ''}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}


          {/* Секция действий */}
          <div className="share-actions-section">
            <button
              className="share-action-btn"
              onClick={() => {
                if (links.length === 0) {
                  toast.error('Сначала создайте ссылку');
                  return;
                }
                if (links.length > 1 && !selectedLinkId) {
                  toast.error('Выберите ссылку для отправки');
                  return;
                }
                handleCopyToMessenger();
              }}
              disabled={distributionLockedForCurrentUser}
            >
              <FiShare2 /> Отправить в месседжер
            </button>

            <button
              className="share-action-btn"
              onClick={() => {
                if (links.length === 0) {
                  toast.error('Сначала создайте ссылку');
                  return;
                }
                if (links.length > 1 && !selectedLinkId) {
                  toast.error('Выберите ссылку для отправки');
                  return;
                }
                setShowEmailForm(true);
              }}
              disabled={distributionLockedForCurrentUser}
            >
              <FiMail /> Отправить на email
            </button>

            <button
              className="share-action-btn share-action-btn-primary"
              onClick={() => {
                resetForm();
                setEditingLink(null);
                setShowCreateForm(true);
              }}
            >
              <FiPlus /> Создать новую ссылку
            </button>
          </div>

          {/* Форма создания/редактирования ссылки */}
          {showCreateForm && (
            <div className="share-form-section">
              <h3>{editingLink ? 'Редактировать ссылку' : 'Создать новую ссылку'}</h3>
              <div className="share-form">
                <div className="form-group">
                  <label className="form-label">Название ссылки *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Например: Для клиентов компании X"
                    required
                  />
                </div>

                <div className="form-group">
                  <div className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.has_password}
                      onChange={(e) => setFormData({ ...formData, has_password: e.target.checked })}
                    />
                    <label>Защитить паролем</label>
                  </div>
                </div>

                {formData.has_password && (
                  <div className="form-group">
                    <label className="form-label">Пароль *</label>
                    <input
                      type="password"
                      className="form-input"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Введите пароль"
                      required={formData.has_password}
                    />
                  </div>
                )}

                <div className="form-group">
                  <div className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.requires_registration || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData({
                          ...formData,
                          requires_registration: checked,
                          access_days: checked ? formData.access_days : '',
                          quiz_enabled: checked ? formData.quiz_enabled : false,
                        });
                        if (checked && newRegistrationFields.length === 0) {
                          addDefaultFieldsToForm();
                        } else if (!checked) {
                          setNewRegistrationFields([]);
                          setQuizQuestions([]);
                        }
                      }}
                    />
                    <label>Требуется регистрация на просмотр</label>
                  </div>
                </div>

                {formData.requires_registration && (
                  <div className="form-group">
                    <label className="form-label">Сколько дней доступно видео после регистрации</label>
                    <input
                      type="number"
                      className="form-input"
                      min="1"
                      max="365"
                      value={formData.access_days}
                      onChange={(e) => setFormData({ ...formData, access_days: e.target.value })}
                      placeholder="Без ограничения"
                    />
                    <small className="form-hint">
                      Отсчёт начинается в момент регистрации. Если оставить пустым — доступ не ограничен
                      (пока действует сама ссылка). У каждого зрителя свой срок: кому-то уже закроем,
                      у кого срок не вышел — запись останется.
                    </small>
                  </div>
                )}

                {formData.requires_registration && (
                  <div className="form-group">
                    <div className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.quiz_enabled || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData({ ...formData, quiz_enabled: checked });
                          if (checked && quizQuestions.length === 0) {
                            setQuizQuestions([emptyQuizQuestion(0)]);
                          }
                        }}
                      />
                      <label>Включить тестирование после просмотра</label>
                    </div>
                    <small className="form-hint">
                      После просмотра записи зритель увидит вопросы. Окно можно закрыть только ответив
                      на все обязательные вопросы.
                    </small>
                  </div>
                )}

                {formData.requires_registration && formData.quiz_enabled && (
                  <div className="registration-fields-form-section quiz-questions-form-section">
                    <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                      Вопросы теста
                    </h4>
                    {quizQuestions.map((question, index) => (
                      <div key={index} className="quiz-editor-question">
                        <div className="quiz-editor-question-header">
                          <span>Вопрос {index + 1}</span>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => removeQuizQuestion(index)}
                            title="Удалить вопрос"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                        <textarea
                          className="form-textarea"
                          rows="2"
                          value={question.text}
                          onChange={(e) => updateQuizQuestion(index, { text: e.target.value })}
                          placeholder="Текст вопроса"
                        />
                        <div className="quiz-editor-row">
                          <select
                            className="form-input"
                            value={question.question_type}
                            onChange={(e) => {
                              const question_type = e.target.value;
                              const updates = { question_type };
                              if (question_type === 'text') {
                                updates.options = [];
                              } else if (!(question.options || []).length) {
                                updates.options = [{ text: 'Вариант 1' }, { text: 'Вариант 2' }];
                              }
                              updateQuizQuestion(index, updates);
                            }}
                          >
                            <option value="single">Один вариант</option>
                            <option value="multiple">Несколько вариантов</option>
                            <option value="text">Свой ответ</option>
                          </select>
                          <label className="form-checkbox">
                            <input
                              type="checkbox"
                              checked={question.is_required}
                              onChange={(e) => updateQuizQuestion(index, { is_required: e.target.checked })}
                            />
                            Обязательный
                          </label>
                        </div>
                        {question.question_type !== 'text' && (
                          <div className="quiz-editor-options">
                            {(question.options || []).map((option, optionIndex) => (
                              <div key={optionIndex} className="quiz-editor-option">
                                <input
                                  type="text"
                                  className="form-input"
                                  value={option.text}
                                  onChange={(e) => updateQuizOption(index, optionIndex, e.target.value)}
                                  placeholder={`Вариант ${optionIndex + 1}`}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline"
                                  onClick={() => removeQuizOption(index, optionIndex)}
                                  title="Удалить вариант"
                                >
                                  <FiMinus />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() => addQuizOption(index)}
                            >
                              <FiPlus /> Добавить вариант
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button type="button" className="btn btn-outline" onClick={addQuizQuestion}>
                      <FiPlus /> Добавить вопрос
                    </button>
                  </div>
                )}

                {/* Форма полей регистрации */}
                {formData.requires_registration && (
                  <div className="registration-fields-form-section">
                    <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                      Поля регистрации
                    </h4>
                    
                    {newRegistrationFields.length === 0 ? (
                      <div style={{ marginBottom: '15px' }}>
                        <button 
                          type="button" 
                          className="btn btn-outline" 
                          onClick={addDefaultFieldsToForm}
                        >
                          <FiPlus /> Добавить стандартные поля
                        </button>
                      </div>
                    ) : (
                      <div className="registration-fields-form-list">
                        {newRegistrationFields.map((field, index) => (
                          <div key={index} className="registration-field-form-item">
                            <div className="field-form-row">
                              <input
                                type="text"
                                className="form-input"
                                value={field.field_name}
                                onChange={(e) => updateRegistrationField(index, { field_name: e.target.value })}
                                placeholder="Название поля"
                                style={{ flex: 1 }}
                              />
                              <select
                                className="form-input"
                                value={field.field_type}
                                onChange={(e) => updateRegistrationField(index, { field_type: e.target.value })}
                                style={{ width: '150px', marginLeft: '10px' }}
                              >
                                <option value="text">Текст</option>
                                <option value="hologram">Голограмма</option>
                              </select>
                              <div className="form-checkbox" style={{ marginLeft: '10px', marginRight: '10px' }}>
                                <input
                                  type="checkbox"
                                  checked={field.is_required}
                                  onChange={(e) => updateRegistrationField(index, { is_required: e.target.checked })}
                                />
                                <label style={{ fontSize: '12px' }}>Обязательное</label>
                              </div>
                              {field.field_type === 'hologram' && (
                                <button
                                  type="button"
                                  className="btn btn-outline"
                                  onClick={addHologramField}
                                  style={{ marginLeft: '5px', padding: '5px 10px' }}
                                  title="Добавить еще поле голограммы"
                                >
                                  <FiPlus />
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => removeRegistrationField(index)}
                                style={{ marginLeft: '5px', padding: '5px 10px', color: '#dc3545' }}
                                title="Удалить поле"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Срок действия (опционально)</label>
                  <div className="form-row">
                    <input
                      type="date"
                      className="form-input"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    />
                    <input
                      type="time"
                      className="form-input"
                      value={formData.expires_at_time}
                      onChange={(e) => setFormData({ ...formData, expires_at_time: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-outline" onClick={cancelEdit}>
                    Отмена
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={editingLink ? handleUpdateLink : handleCreateLink}
                    disabled={loading}
                  >
                    {editingLink ? 'Сохранить' : 'Создать'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Форма отправки email */}
          {showEmailForm && (
            <div className="share-form-section">
              <h3>Отправить на email</h3>
              <div className="share-form">
                <div className="form-group">
                  <label className="form-label">Адреса получателей *</label>
                  <textarea
                    className="form-textarea"
                    rows="5"
                    value={emailList}
                    onChange={(e) => setEmailList(e.target.value)}
                    placeholder="a@mail.ru b@mail.ru или через запятую: a@mail.ru, b@mail.ru"
                    required
                  />
                  <small className="form-hint">
                    Укажите email через запятую, пробел или с новой строки. Все получатели в скрытой копии (BCC) —
                    адреса друг у друга не видны.
                  </small>
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-outline"
                    onClick={() => {
                      setShowEmailForm(false);
                      setEmailList('');
                    }}
                  >
                    Отмена
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleSendEmail()}
                    disabled={loading}
                  >
                    Отправить
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Настройки полей регистрации */}
          {showRegistrationFields && editingLinkForFields && (
            <div className="share-form-section">
              <h3>Настройки полей регистрации: {editingLinkForFields.name}</h3>
              
              {registrationFields.length === 0 ? (
                <div style={{ marginBottom: '20px' }}>
                  <p>Поля регистрации не настроены. Добавьте стандартные поля или создайте свои.</p>
                  <button className="btn btn-outline" onClick={addDefaultFields}>
                    Добавить стандартные поля
                  </button>
                </div>
              ) : (
                <div className="registration-fields-list">
                  {registrationFields.map((field, index) => (
                    <div key={field.id} className="registration-field-item">
                      <div className="field-info">
                        <span className="field-order">{index + 1}.</span>
                        <span className="field-name">{field.field_name}</span>
                        <span className={`field-type-badge ${field.field_type}`}>
                          {field.field_type === 'hologram' ? 'Голограмма' : 'Текст'}
                        </span>
                        {field.is_required && <span className="field-required-badge">Обязательное</span>}
                      </div>
                      <div className="field-actions">
                        <button
                          className="share-link-btn"
                          onClick={() => {
                            const newFields = [...registrationFields];
                            newFields[index] = { ...field, is_required: !field.is_required };
                            handleSaveRegistrationField(newFields[index]);
                          }}
                          title={field.is_required ? 'Сделать необязательным' : 'Сделать обязательным'}
                        >
                          {field.is_required ? '✓' : '○'}
                        </button>
                        <button
                          className="share-link-btn share-link-btn-danger"
                          onClick={() => handleDeleteRegistrationField(field.id)}
                          title="Удалить поле"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button className="btn btn-outline" onClick={() => {
                  setShowRegistrationFields(false);
                  setEditingLinkForFields(null);
                  setRegistrationFields([]);
                }}>
                  Закрыть
                </button>
                {registrationFields.length === 0 && (
                  <button className="btn btn-primary" onClick={addDefaultFields}>
                    Добавить стандартные поля
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
